import {
    Badge,
    Box,
    Button,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { DateTimeColumn, SearchableSelect, TextColumn } from '../../common';
import { getUserBillings, getUsers } from '../../../http/apiClient';
import { useEffect, useMemo, useState } from 'react';

import CostColumn from './Columns/CostColumn';
import CountColumn from './Columns/CountColumn';
import { DatePickerInput } from '@mantine/dates';
import { MantineReactTable } from 'mantine-react-table';
import MessageGroupColumn from './Columns/MessageGroupColumn';
import dayjs from 'dayjs';
import { hasRole } from '../../../utils/roles';
import { useAuthStore } from '../../../store';
import { useDebouncedCallback } from '@mantine/hooks';
import { usePaginatedQuery } from '../../../http/queries/usePaginatedQuery';

const ADJUSTMENT_TYPES = ['Credit', 'VolumeDiscount', 'TopUp'];

const TYPE_LABEL = {
    Credit: 'Credit',
    VolumeDiscount: 'Bulk Discount',
    TopUp: 'TopUp',
};

const buildBillingRows = (billingData) => {
    const billings = [];
    // Map key: `${date}|${typeLabel}`
    const adjustmentAgg = new Map();

    for (const item of billingData) {
        if (!ADJUSTMENT_TYPES.includes(item.type)) {
            // Normal billing rows go through untouched
            billings.push(item);
            continue;
        }

        const typeLabel = TYPE_LABEL[item.type];
        const dateKey = item.date; // assume this is already a normalized date string
        const mapKey = `${dateKey}|${typeLabel}`;

        // Normalize values (negate totalCost, null campaignName)
        const count = item.count || 0;
        const concatenatedCount = item.concatenatedCount || 0;
        const totalCost = item.totalCost ? item.totalCost * -1 : 0;

        if (!adjustmentAgg.has(mapKey)) {
            adjustmentAgg.set(mapKey, {
                // Base shape of the aggregated record
                date: dateKey,
                type: typeLabel,
                campaignName: null,
                count: 0,
                concatenatedCount: 0,
                totalCost: 0,
            });
        }

        const agg = adjustmentAgg.get(mapKey);
        agg.count += count;
        agg.concatenatedCount += concatenatedCount;
        agg.totalCost += totalCost;
    }

    // Flatten aggregated adjustments; optionally sort by date
    const aggregatedAdjustments = Array.from(adjustmentAgg.values()).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );
    return [...billings, ...aggregatedAdjustments];
};

const UserBillingModal = ({ user, opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);

    const [userId, setUserId] = useState(user?.id || userProfile.id);
    const [dateRange, setDateRange] = useState([
        dayjs().toDate(),
        dayjs().toDate(),
    ]);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 200,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    useEffect(() => {
        if (user?.id) {
            setUserId(user.id);
        }
    }, [user?.id]);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) =>
            getUserBillings({
                pagination: {
                    ...queryParams,
                    start: dateRange[0],
                    end: dateRange[1],
                },
                userId,
            }),
        queryKey: ['user_billings', userId, `${dateRange[0]}-${dateRange[1]}`],
        refetchInterval: 5_000,
        globalFilter,
        pagination,
        sorting,
        enabled: opened && dateRange.length >= 2,
    });

    const { data: billingData, pagination: dataPagination } =
        paginatedData || {};

    const columns = useMemo(
        () =>
            [
                TextColumn({
                    header: 'Campaign',
                    accessorKey: 'campaignName',
                    columnProps: { maxSize: 120, minSize: 60 },
                }),
                userProfile?.username === 'pixelforge'
                    ? MessageGroupColumn({
                          header: 'Message Group',
                          accessorKey: 'messageGroup',
                      })
                    : null,
                TextColumn({
                    header: 'Type',
                    accessorKey: 'type',
                    columnProps: { maxSize: 120, minSize: 60 },
                }),
                CountColumn({
                    header: 'Count',
                    accessorKey: 'count',
                    columnProps: { maxSize: 50, minSize: 40 },
                }),
                CountColumn({
                    header: 'Total',
                    accessorKey: 'concatenatedCount',
                    columnProps: { maxSize: 50, minSize: 40 },
                }),
                CostColumn({
                    header: 'Cost',
                    accessorKey: 'totalCost',
                    columnProps: { maxSize: 50, minSize: 40 },
                }),
                DateTimeColumn({
                    id: 'date',
                    header: 'Date',
                    accessorFn: (rowData) => rowData?.date,
                    format: 'L',
                }),
            ].filter((x) => x != null),
        [billingData]
    );

    const tableData = useMemo(() => {
        if (isLoading || !billingData) {
            return [];
        }

        const records = buildBillingRows(billingData);
        return records.filter((x) => x != null);
    }, [billingData, isLoading]);

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: false,
        enableColumnResizing: false,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: false,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        positionActionsColumn: 'last',
        enableStickyFooter: true,
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Billing',
        },
        mantineTableContainerProps: {
            style: {
                minHeight: 500,
            },
        },
        mantinePaginationProps: {
            rowsPerPageOptions: ['25', '50', '100', '200'],
        },
        positionGlobalFilter: 'left',

        renderTopToolbarCustomActions: () => (
            <Group align={'flex-start'}>
                {hasRole(userProfile, 'SUPER_ADMIN') ? (
                    <SearchableSelect
                        queryHttpFn={getUsers}
                        queryKey={'users'}
                        optionsMapFn={(user) => ({
                            label: user.username,
                            value: user.id.toString(),
                        })}
                        value={userId}
                        defaultValue={{
                            label: userProfile.username,
                            value: userProfile.id,
                        }}
                        onChange={setUserId}
                    />
                ) : null}
                <DatePickerInput
                    type='range'
                    placeholder='Date range'
                    valueFormat={'MM/DD/YYYY'}
                    allowSingleDateInRange={true}
                    value={dateRange}
                    onChange={setDateRange}
                />
            </Group>
        ),

        manualPagination: true,
        manualSorting: true,
        //onColumnVisibilityChange: handleVisibilityChange,
        //onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'auto'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Billing Usage</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <MantineReactTable
                        {...tableProps}
                        enableTableFooter={true}
                    />
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default UserBillingModal;
